import React, { useState, useEffect } from 'react';
import {
  LayoutTemplate,
  Save,
  Loader2,
  Upload,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { useContentBlocks, ContentBlock } from '../../lib/hooks/useContentBlocks';
import MediaPicker from '../../components/admin/MediaPicker';
import TextEditor from '../../components/TextEditor';
import { toDisplayHtml } from '../../lib/richText';

type BlockKey = 'textImage' | 'imageText';

const BLOCK_META: { key: BlockKey; title: string; hint: string }[] = [
  { key: 'textImage', title: 'Секция: Текст (ляво) + Снимка (дясно)', hint: 'текст отляво, снимка отдясно' },
  { key: 'imageText', title: 'Секция: Снимка (ляво) + Текст (дясно)', hint: 'снимка отляво, текст отдясно' },
];

const ContentBlocksEditor = () => {
  const { blocks, loading, saveBlocks } = useContentBlocks();
  const [form, setForm] = useState<{ textImage: ContentBlock; imageText: ContentBlock }>({
    textImage: blocks.textImage,
    imageText: blocks.imageText,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState<BlockKey | null>(null);
  const [uploadingFor, setUploadingFor] = useState<BlockKey | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });

  useEffect(() => {
    if (!loading) {
      // Старият обикновен текст се показва като абзаци в редактора.
      setForm({
        textImage: { ...blocks.textImage, text: toDisplayHtml(blocks.textImage.text) },
        imageText: { ...blocks.imageText, text: toDisplayHtml(blocks.imageText.text) },
      });
    }
  }, [blocks, loading]);

  const update = (key: BlockKey, field: keyof ContentBlock, value: string) => {
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const uploadImage = async (key: BlockKey, file: File) => {
    if (!file.type.startsWith('image/')) {
      setStatus({ type: 'error', message: 'Моля, качете изображение' });
      return;
    }
    try {
      setUploadingFor(key);
      const timestamp = Date.now();
      const safeName = encodeURIComponent(file.name).replace(/%/g, '_');
      const storageRef = ref(storage, `homepage/block_${timestamp}_${safeName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      update(key, 'imageUrl', url);
    } catch (err) {
      console.error('Error uploading block image:', err);
      setStatus({ type: 'error', message: 'Грешка при качване на снимката' });
    } finally {
      setUploadingFor(null);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setStatus({ type: null, message: '' });
    const ok = await saveBlocks(form);
    setIsSaving(false);
    setStatus(
      ok
        ? { type: 'success', message: 'Секциите са запазени успешно!' }
        : { type: 'error', message: 'Грешка при запазване' }
    );
    if (ok) setTimeout(() => setStatus({ type: null, message: '' }), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const renderBlock = (key: BlockKey, title: string, hint: string) => {
    const data = form[key];
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-lg font-semibold mb-1 text-gray-800">{title}</h2>
        <p className="text-xs text-gray-400 mb-6">Разположение: {hint}</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Малко заглавие (над основното)</label>
              <input
                type="text"
                value={data.subtitle}
                onChange={(e) => update(key, 'subtitle', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="напр. За комплекса"
                disabled={isSaving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Заглавие</label>
              <input
                type="text"
                value={data.title}
                onChange={(e) => update(key, 'title', e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Заглавие на секцията"
                disabled={isSaving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Текст</label>
              <TextEditor
                value={data.text}
                onChange={(text) => update(key, 'text', text)}
                placeholder="Описание..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Бутон (текст) — по избор</label>
                <input
                  type="text"
                  value={data.buttonText}
                  onChange={(e) => update(key, 'buttonText', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="напр. Научете повече"
                  disabled={isSaving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Бутон (линк)</label>
                <input
                  type="text"
                  value={data.buttonLink}
                  onChange={(e) => update(key, 'buttonLink', e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="/apartments"
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Снимка</label>
            <div className="aspect-video w-full rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center overflow-hidden mb-3">
              {data.imageUrl ? (
                <img src={data.imageUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm text-gray-400">Няма снимка</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm">
                {uploadingFor === key ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Качи
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={isSaving || uploadingFor === key}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(key, file);
                  }}
                />
              </label>
              <button
                type="button"
                onClick={() => setPickerFor(key)}
                className="inline-flex items-center px-3 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors text-sm"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                От медия
              </button>
              {data.imageUrl && (
                <button
                  type="button"
                  onClick={() => update(key, 'imageUrl', '')}
                  className="inline-flex items-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  <X className="w-4 h-4 mr-2" />
                  Премахни
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div className="flex items-center">
          <LayoutTemplate className="w-8 h-8 text-primary mr-3" />
          <h1 className="text-2xl font-bold">Текстови секции</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Запазване...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Запази промените
            </>
          )}
        </button>
      </div>

      {status.type && (
        <div
          className={`mb-6 p-4 rounded-lg border ${
            status.type === 'success'
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          {status.message}
        </div>
      )}

      <div className="space-y-6">
        {BLOCK_META.map((b) => (
          <React.Fragment key={b.key}>{renderBlock(b.key, b.title, b.hint)}</React.Fragment>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
        Редът и видимостта на тези секции се управляват от „Подредба на секции".
      </div>

      <MediaPicker
        isOpen={pickerFor !== null}
        onClose={() => setPickerFor(null)}
        onSelect={(url) => {
          if (pickerFor) update(pickerFor, 'imageUrl', url);
        }}
        title="Избери снимка за секцията"
      />
    </div>
  );
};

export default ContentBlocksEditor;
